import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import useAuth from '../../auth/feature/auth';
import { homeService } from '../service/home.service';
import {
  AR_CONSENT_VERSION,
  MAX_CHECKOUT_QUANTITY,
  ROUTER_LINKS
} from '../../common/consts/app-keys.const';
import { useThrottle } from '../../hooks/useThrottle';

/**
 * The EULA the checkout panel points a buyer at, per product. Product names are
 * proper nouns and stay untranslated, matching every other reference to them.
 *
 * A map rather than the `isUnrealProduct ? subdivs : touchpose` ternary this
 * replaces: that ternary was correct only while exactly two products reached
 * checkout, and quietly told the third (the Eye) it was buying under the
 * TouchPose EULA. An unlisted product still falls back to TouchPose, which is
 * the default product this card was originally written for.
 */
const EULA_BY_PRODUCT: Record<string, { href: string; label: string }> = {
  'squarebit-subdivs': {
    href: '/squarebit-subdivs/eula',
    label: 'Squarebit Subdivs EULA'
  },
  'squarebit-eye': { href: '/squarebit-eye/eula', label: 'Squarebit Eye EULA' }
};

const DEFAULT_EULA = { href: '/touchpose/eula', label: 'TouchPose EULA' };

/**
 * The numeric value of a price the card is DISPLAYING, or null if it has none.
 *
 * `Number()` was used here and it silently broke the seat-total line. The
 * prices these cards receive are display strings from the locale files, and
 * the Eye's are "$15" and "$0" - `Number("$15")` is NaN, so the total was
 * never finite and the line simply never rendered. Nothing failed; a row of
 * the UI just stopped existing, which is why it survived a price change.
 *
 * Deliberately lenient about presentation and strict about meaning: a currency
 * symbol, spaces and thousands separators are stripped, and a comma decimal is
 * accepted because several of the nine locales write 15,00 - but anything that
 * is not a number after that ("Custom", "N/A", "") returns null, so the caller
 * omits the line instead of printing NaN at someone.
 */
/**
 * Whether a displayed price still needs a "$" put in front of it.
 *
 * Keyed off the VALUE, not the card's mode, because mode is what got this
 * wrong twice. It first added the prefix only in checkout mode, so one "$15"
 * rendered as "$15" on a contact card and "$$15" on a checkout card. Fixing
 * that by never prefixing a customPrice then left the three TouchPose cards -
 * which pass a bare "10" - showing a price with no currency on it at all.
 *
 * A bare number is a price from Stripe and needs the symbol. Anything already
 * carrying one, and anything that is not a number ("Custom", "N/A"), is left
 * exactly as it is. The test is deliberately "digits and separators only", so
 * a locale that ever writes "15 EUR" is not given a dollar sign as well.
 */
export const needsCurrencyPrefix = (value: unknown): boolean =>
  typeof value === 'string' && /^[\d.,]+$/.test(value.trim());

export const parseDisplayPrice = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  // Keep digits, separators and a sign; drop currency symbols and spacing,
  // including the non-breaking kind that fr and ru actually use.
  const cleaned = value.replace(/[^\d.,-]/g, '');
  if (!cleaned) return null;
  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  const normalised =
    lastComma > -1 && lastComma > lastDot
      ? cleaned.replace(/\./g, '').replace(',', '.') // 1.234,56 -> 1234.56
      : cleaned.replace(/,/g, ''); //                    1,234.56 -> 1234.56
  const parsed = Number(normalised);
  return Number.isFinite(parsed) ? parsed : null;
};


interface IProps {
  caption: string;
  duration: string;
  /** One-paragraph blurb. Omit when using `features` (a bullet list) instead. */
  description?: string;
  /** Italic tagline under the caption (e.g. "For indie devs shipping commercial work"). */
  tagline?: string;
  /** Bullet-point feature list — used instead of `description` when provided. */
  features?: string[];
  /** Small italic fine print above the button (e.g. revenue-tier eligibility). */
  finePrint?: string;
  plan: '1m' | '1y' | 'free' | 'perpetual';
  customPrice?: string;
  badgeText?: string;
  buttonText?: string;
  statValue?: string;
  statLabel?: string;
  isPopular?: boolean;
  /** Product slug for multi-product checkout. Omit for the default (TouchPose). */
  product?: string;
  /**
   * Enterprise/contact variant: the button becomes a plain link (e.g. mailto)
   * instead of a checkout flow — no price fetch, no consent panel, and the
   * price area renders customPrice verbatim (no "$" prefix).
   */
  contactHref?: string;
  /**
   * Custom CTA action. When provided, the button calls this instead of the
   * checkout flow or a contactHref link (used for the Subdivs free-tier claim).
   */
  onCta?: () => void;
  /** Shows a busy spinner and disables the CTA while `onCta` is in flight. */
  ctaBusy?: boolean;
  /**
   * Offer a seat count on this card, carried into Stripe as the line-item
   * quantity (and from there into the issued licence's seat count).
   *
   * Opt-in per card rather than on by default: a card whose price is a word
   * rather than a number ("Custom", "$0") has nothing to multiply, and a
   * stepper on it would imply a self-serve order that does not exist.
   */
  seatPicker?: boolean;
}

export const SubscriptionPlan = ({
  caption,
  duration,
  plan,
  description,
  tagline,
  features,
  finePrint,
  customPrice,
  badgeText,
  buttonText,
  statValue,
  statLabel,
  isPopular,
  product,
  contactHref,
  onCta,
  ctaBusy,
  seatPicker
}: IProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  // Auto-renewal consent gate (ROSCA / state auto-renewal statutes): paid
  // plans must show the recurring-charge disclosure and get a separate,
  // affirmative checkbox tick BEFORE redirecting to Stripe checkout.
  const [showConsent, setShowConsent] = React.useState(false);
  const [arAgreed, setArAgreed] = React.useState(false);
  // Unreal Engine products additionally require the buyer to affirm they
  // hold a valid UE license for the matching engine version (UE EULA
  // §5(a)(i)); enforced server-side too.
  const isUnrealProduct = product === 'squarebit-subdivs';
  const eula = EULA_BY_PRODUCT[product ?? ''] ?? DEFAULT_EULA;
  const [ueAgreed, setUeAgreed] = React.useState(false);
  // One-time ($99 perpetual) purchase: no auto-renewal, so the AR disclosure/consent doesn't apply —
  // only the UE-licensee affirmation gates it. Provisioned via mode:'payment' + the
  // checkout.session.completed webhook. Price is shown from customPrice (no live subscription lookup).
  const isOneTime = plan === 'perpetual';

  // Seats. Only a card that asked for the picker AND actually reaches checkout
  // gets one — a contact link or a custom CTA never sends a quantity anywhere.
  const showSeatPicker = Boolean(seatPicker) && !contactHref && !onCta;
  const [seats, setSeats] = React.useState(1);
  const clampSeats = (n: number) =>
    Math.min(MAX_CHECKOUT_QUANTITY, Math.max(1, Math.trunc(n)));

  const price = useQuery({
    queryKey: ['price', plan, product ?? 'default'],
    queryFn: async () => homeService.getPlanPrice(plan, product),
    enabled: !contactHref && !isOneTime
  });

  const displayPrice = customPrice ?? price.data ?? 'N/A';
  // Only when the price is a plain number. "Custom" and "N/A" multiply into
  // nonsense, so the line is omitted rather than guessed at.
  const unitPrice = parseDisplayPrice(displayPrice);
  const seatTotal =
    unitPrice !== null
      ? (unitPrice * seats).toFixed(2).replace(/\.00$/, '')
      : null;
  const arPeriod = t(
    plan === '1y' ? 'subscription.arPeriodYearly' : 'subscription.arPeriodMonthly'
  );

  const getSessionUrl = useMutation({
    mutationKey: ['getSessionUrl', product ?? 'default'],
    mutationFn: () =>
      homeService.getCheckoutSession(
        plan,
        product,
        {
          version: AR_CONSENT_VERSION,
          locale: i18n.language,
          price: String(displayPrice),
          ueLicensee: isUnrealProduct ? true : undefined
        },
        // Clamped at the point of use, not just on blur: an empty field reads
        // as 0 for the instant before blur fires, and 0 must never be what
        // leaves for Stripe.
        showSeatPicker ? clampSeats(seats) : undefined
      ),
    onSuccess: (data) => {
      window.location.href = data;
    },
    onError: () => {
      toast.error(t('subscription.checkoutError'));
    }
  });
  const throttledGetSessionUrl = useThrottle(getSessionUrl.mutate);

  const getKey = useMutation({
    mutationKey: ['getKey'],
    mutationFn: () => homeService.getFreeKey(),
    onSuccess: () => {
      toast.success(t('subscription.keyGenerated'));
      queryClient.invalidateQueries({ queryKey: ['keys'] });
      navigate(ROUTER_LINKS.MANAGEMENT);
    },
    onError: (err: any) => {
      const message =
        err.response?.data?.message ?? t('subscription.trialAlreadyUsed');
      toast.error(message);
    }
  });
  const throttledGetKey = useThrottle(getKey.mutate);

  const buySubscriptionHandler = () => {
    if (!isAuthenticated) {
      navigate(
        plan === 'free'
          ? `${ROUTER_LINKS.SIGNUP}?trial=touchpose`
          : ROUTER_LINKS.SIGNUP
      );
      return;
    }
    if (plan === 'free') {
      throttledGetKey();
      return;
    }
    // First click reveals the disclosure/affirmation panel; only a second click with the required
    // box(es) ticked proceeds to Stripe. One-time perpetual needs only the UE-licensee affirmation
    // (no auto-renewal), subscriptions need the AR consent (+ UE affirmation for Unreal products).
    if (!showConsent) {
      setShowConsent(true);
      return;
    }
    const consentOk = isOneTime
      ? !isUnrealProduct || ueAgreed
      : arAgreed && (!isUnrealProduct || ueAgreed);
    if (consentOk) throttledGetSessionUrl();
  };

  const isBusy = plan === 'free' ? getKey.isPending : getSessionUrl.isPending;
  const awaitingConsent = Boolean(
    plan !== 'free' &&
    isAuthenticated &&
    showConsent &&
    (isOneTime
      ? isUnrealProduct && !ueAgreed
      : !arAgreed || (isUnrealProduct && !ueAgreed))
  );
  const defaultLabel =
    plan === 'free' ? t('subscription.startFreeTrial') : `Buy ${caption}`;
  const busyLabel =
    plan === 'free' ? t('subscription.settingUp') : t('subscription.redirecting');
  const consentLabel =
    plan !== 'free' && isAuthenticated && showConsent
      ? t('subscription.arContinue')
      : undefined;
  const label = isBusy ? busyLabel : (consentLabel ?? buttonText ?? defaultLabel);

  const cardBase =
    'bg-surface-base relative rounded-[16px] border flex flex-col p-[22px_22px_24px] transition-all duration-250 overflow-hidden';
  const cardNormal = `${cardBase} border-border-dark/[0.11] shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.1),0_2px_8px_rgba(0,0,0,0.05)] hover:-translate-y-[2px] hover:border-[rgba(14,165,201,0.25)]`;
  const cardPopular = `${cardBase} border-blue shadow-[0_4px_20px_rgba(14,165,201,0.15)] hover:shadow-[0_12px_40px_rgba(14,165,201,0.2)] hover:-translate-y-[2px]`;

  const btnBase =
    'flex items-center justify-center gap-[5px] py-[11px] px-[20px] rounded-[9px] text-[13px] font-medium cursor-pointer transition-all duration-200 no-underline tracking-[0.005em]';
  const btnNormal = `${btnBase} bg-surface-section border-[1.5px] border-border-dark/[0.11] text-content-dark shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)] hover:bg-surface-hover-section hover:border-blue hover:text-surface-popular`;
  const btnPrimary = `${btnBase} bg-blue border-[1.5px] border-blue text-surface-base font-semibold shadow-[0_2px_10px_rgba(14,165,201,0.3)] hover:bg-surface-popular hover:border-surface-popular hover:shadow-[0_4px_18px_rgba(14,165,201,0.35)]`;

  // A free-plan CTA is auth-aware: a logged-out visitor is sent to sign up, but a
  // logged-in user goes straight to My Products (where the free download lives).
  // Sending an already-authenticated user to /auth/signup just bounces them to home.
  const resolvedHref =
    contactHref && plan === 'free' && isAuthenticated
      ? ROUTER_LINKS.MANAGEMENT
      : contactHref;

  return (
    <div
      className={isPopular ? cardPopular : cardNormal}
      style={
        isPopular
          ? {
              background:
                'linear-gradient(170deg, var(--subscription-brand-100) 25%, var(--color-bg-base) 75%)'
            }
          : undefined
      }
    >
      {/* Corner ribbon */}
      {badgeText && (
        <div className="absolute top-[23px] right-[-27px] w-[140px] bg-blue text-surface-base font-mono text-[8.5px] font-400 tracking-wider uppercase text-center pl-[12px] pr-[5px] py-[5px] rotate-[40deg] shadow-[0_2px_8px_rgba(14,165,201,0.4)] whitespace-nowrap">
          {badgeText}
        </div>
      )}

      <div className="font-mono text-[12px] tracking-[0.16em] uppercase text-content-muted-alt mb-[6px]">
        {caption}
      </div>

      {tagline && (
        <p className="font-body italic text-[13px] text-content-muted-alt leading-[1.45] mb-[13px]">
          {tagline}
        </p>
      )}

      <div className="mb-[4px]">
        <div>
          <span className="font-display font-extrabold text-[38px] leading-[1.2] tracking-[-0.04em] text-content-dark">
            {/* A customPrice is a LITERAL from the locale files and carries
                its own currency symbol ("$0", "$15"), so it renders verbatim.
                Only a price looked up from Stripe is a bare number and needs
                the prefix.

                It used to key off checkout-vs-link mode instead, which meant
                the same "$15" rendered as "$15" on a contact card and "$$15"
                on a checkout card — so wiring a card to checkout silently
                required editing nine locale files, and left them inconsistent
                ("$0" next to "15") for no reason a reader could see. */}
            {needsCurrencyPrefix(displayPrice)
              ? `$${displayPrice}`
              : displayPrice}
          </span>
        </div>
        <div className="font-body text-[13px] text-content-muted-alt">
          {duration}
        </div>
      </div>

      <div className="h-px bg-border-dark/[0.07] my-[14px]" />

      {features && features.length > 0 ? (
        <ul className="flex-1 mb-[18px] space-y-[8px]">
          {features.map((f, i) => (
            <li
              key={i}
              className="flex items-start gap-[8px] font-body text-[13px] text-content-muted-alt leading-[1.5]"
            >
              <svg
                className="mt-[3px] shrink-0 text-blue"
                width="13"
                height="13"
                viewBox="0 0 14 14"
                fill="none"
              >
                <path
                  d="M11.5 4L5.5 10L2.5 7"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{f}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="font-body text-[13px] text-content-muted-alt leading-[1.65] flex-1 mb-[20px]">
          {description}
        </p>
      )}

      {finePrint && (
        <p className="font-body italic text-[11.5px] text-content-muted-alt leading-[1.5] mb-[16px]">
          {finePrint}
        </p>
      )}

      {statValue && (
        <div className="mb-[20px]">
          <div
            className={`font-display text-[22px] font-bold tracking-[-0.02em] leading-[1.3] pb-[10px] min-h-[28px] whitespace-pre-line ${isPopular ? 'text-surface-popular' : 'text-content-dark'}`}
          >
            {statValue}
          </div>
          {statLabel && (
            <div className="font-body text-[12px] text-content-muted-alt mt-[2px]">
              {statLabel}
            </div>
          )}
        </div>
      )}

      {/* Seats. Above the consent panel on purpose: the disclosure below
          restates the price the buyer is about to be charged, so the number
          it multiplies has to already be chosen and visible. */}
      {showSeatPicker && (
        <div className="mb-[14px] rounded-[10px] border border-border-dark/[0.11] bg-surface-section p-[12px] text-left">
          <div className="flex items-center justify-between gap-[10px]">
            <div>
              <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-content-muted-alt">
                {t('subscription.seatsTitle')}
              </div>
              <p className="font-body text-[11.5px] text-content-muted-alt leading-[1.5] mt-[3px]">
                {t('subscription.seatsHelp')}
              </p>
            </div>
            <div className="flex items-center gap-[6px] shrink-0">
              <button
                type="button"
                aria-label={t('subscription.seatsDecrease')}
                disabled={seats <= 1}
                onClick={() => setSeats((n) => clampSeats(n - 1))}
                className="w-[26px] h-[26px] rounded-[7px] border border-border-dark/[0.15] text-[14px] leading-none text-content-muted-alt hover:bg-surface-hover-section disabled:opacity-40 transition-colors"
              >
                −
              </button>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={MAX_CHECKOUT_QUANTITY}
                aria-label={t('subscription.seatsTitle')}
                value={seats}
                onChange={(e) => {
                  // Kept raw while typing so a half-typed "1" on the way to
                  // "12" is not snapped back; the clamp lands on blur.
                  const next = Number(e.target.value);
                  if (Number.isFinite(next)) setSeats(Math.trunc(next));
                }}
                onBlur={() => setSeats((n) => clampSeats(n))}
                className="w-[52px] text-center py-[3px] rounded-[7px] border border-border-dark/[0.15] bg-surface-base text-[13px] font-medium text-content-dark"
              />
              <button
                type="button"
                aria-label={t('subscription.seatsIncrease')}
                disabled={seats >= MAX_CHECKOUT_QUANTITY}
                onClick={() => setSeats((n) => clampSeats(n + 1))}
                className="w-[26px] h-[26px] rounded-[7px] border border-border-dark/[0.15] text-[14px] leading-none text-content-muted-alt hover:bg-surface-hover-section disabled:opacity-40 transition-colors"
              >
                +
              </button>
            </div>
          </div>
          {seatTotal !== null && seats > 1 && (
            <p className="font-body text-[12px] font-medium text-content-dark leading-[1.5] mt-[10px]">
              {t('subscription.seatsSummary', {
                seats,
                total: seatTotal
              })}
            </p>
          )}
          <p className="font-body italic text-[11px] text-content-muted-alt leading-[1.5] mt-[8px]">
            {t('subscription.seatsMax', { max: MAX_CHECKOUT_QUANTITY })}
          </p>
        </div>
      )}

      {/* Auto-renewal consent: disclosure must stay in visual proximity to
          the consent checkbox, and the checkbox must be separate from any
          other agreement (Cal. Bus. & Prof. Code § 17602; ROSCA). */}
      {plan !== 'free' && isAuthenticated && showConsent && (
        <div className="mb-[14px] rounded-[10px] border border-border-dark/[0.11] bg-surface-section p-[12px] text-left">
          <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-content-muted-alt mb-[6px]">
            {isOneTime
              ? t('subscription.oneTimeConfirmTitle')
              : t('subscription.arTitle')}
          </div>
          {!isOneTime && (
            <>
              <p className="font-body text-[12px] text-content-muted-alt leading-[1.55] mb-[10px]">
                {t('subscription.arDisclosure', {
                  price: displayPrice,
                  period: arPeriod
                })}
              </p>
              <label className="flex items-start gap-[8px] cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="mt-[1px] h-[16px] w-[16px] shrink-0 accent-blue-600 cursor-pointer"
                  checked={arAgreed}
                  onChange={(e) => setArAgreed(e.target.checked)}
                />
                <span className="font-body text-[12px] font-medium text-content-dark leading-[1.5]">
                  {t('subscription.arAgree', {
                    price: displayPrice,
                    period: arPeriod
                  })}
                </span>
              </label>
            </>
          )}
          {isUnrealProduct && (
            <label className="flex items-start gap-[8px] cursor-pointer select-none mt-[8px]">
              <input
                type="checkbox"
                className="mt-[1px] h-[16px] w-[16px] shrink-0 accent-blue-600 cursor-pointer"
                checked={ueAgreed}
                onChange={(e) => setUeAgreed(e.target.checked)}
              />
              <span className="font-body text-[12px] font-medium text-content-dark leading-[1.5]">
                {t('subscription.ueLicenseeAgree')}
              </span>
            </label>
          )}
          <p className="font-body text-[11px] text-content-muted-alt leading-[1.5] mt-[8px]">
            {isOneTime
              ? t('subscription.oneTimeGovernedBy')
              : t('subscription.arGovernedBy')}{' '}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-content-dark"
            >
              {t('auth.termsOfService')}
            </a>{' '}
            {t('auth.and')}{' '}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-content-dark"
            >
              {t('auth.privacyPolicy')}
            </a>
            . {t('subscription.eulaGovernedBy')}{' '}
            <a
              href={eula.href}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-content-dark"
            >
              {eula.label}
            </a>
            .
          </p>
        </div>
      )}

      {onCta ? (
        // Custom action (e.g. the Subdivs free-tier claim) — a button, not a link.
        <button
          type="button"
          className={`${isPopular ? btnPrimary : btnNormal} ${ctaBusy ? 'opacity-60 cursor-wait' : ''}`}
          onClick={onCta}
          disabled={ctaBusy}
        >
          {buttonText}
          {ctaBusy && (
            <svg
              className="animate-spin ml-[4px] shrink-0"
              width="13"
              height="13"
              viewBox="0 0 14 14"
              fill="none"
            >
              <circle
                cx="7"
                cy="7"
                r="5"
                stroke="currentColor"
                strokeWidth="2"
                strokeOpacity="0.3"
              />
              <path
                d="M12 7a5 5 0 0 0-5-5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          )}
        </button>
      ) : contactHref ? (
        // Internal routes go through the SPA router; external/mailto get <a>.
        resolvedHref!.startsWith('/') ? (
          <Link to={resolvedHref!} className={isPopular ? btnPrimary : btnNormal}>
            {buttonText}
          </Link>
        ) : (
          <a href={resolvedHref!} className={isPopular ? btnPrimary : btnNormal}>
            {buttonText}
          </a>
        )
      ) : (
        <button
          type="button"
          className={`${isPopular ? btnPrimary : btnNormal} ${isBusy ? 'opacity-60 cursor-wait' : ''} ${awaitingConsent ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={buySubscriptionHandler}
          disabled={isBusy || awaitingConsent}
        >
          {label}
          {isBusy && (
            <svg
              className="animate-spin ml-[4px] shrink-0"
              width="13"
              height="13"
              viewBox="0 0 14 14"
              fill="none"
            >
              <circle
                cx="7"
                cy="7"
                r="5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeDasharray="10 6"
              />
            </svg>
          )}
        </button>
      )}
    </div>
  );
};
